"use client";

import { AlertCircle, Lock } from "lucide-react";
import { CommentEvents, trackEvent } from "@/analytics";

import { Comedian } from "@/data/comedians";
import { CommentAuthorComedian } from "@punchup/data/models";
import CommentErrorBoundary from "./CommentErrorBoundary";
import CommentInput from "./CommentInput";
import CommentList from "./CommentList";
import Link from "next/link";
import MobileCommentModal from "./MobileCommentModal";
import { SpecialRow } from "@/data/specials";
import { User } from "@supabase/supabase-js";
import { useComments } from "@/hooks/useComments";
import { useEffect } from "react";
import { useFeatureGate } from "statsig-react";

interface CommentSectionProps {
  special: SpecialRow;
  comedian: Comedian;
  userFollowsComedian: boolean;
  isLoggedIn: boolean;
  currentUser?: User | null;
}

// Re-export Comment type from hook
export type { Comment } from "@/hooks/useComments";

export default function CommentSection({
  special,
  comedian,
  userFollowsComedian,
  isLoggedIn,
  currentUser,
}: CommentSectionProps) {
  const { gate } = useFeatureGate("video_comments_stableid");
  const isCommentsEnabled = gate.value === true;

  const {
    comments,
    isLoading,
    error,
    pagination,
    postComment,
    isPosting,
    postError,
    editComment,
    isEditing,
    editError,
    deleteComment,
    isDeleting,
    deleteError,
    loadMore,
    refresh,
    hasMore,
    newCommentsAvailable,
    rateLimitError,
  } = useComments({
    contentId: special.id,
    currentUser,
    enabled: isCommentsEnabled,
  });

  // Track comment section loaded event
  useEffect(() => {
    if (isCommentsEnabled) {
      trackEvent(CommentEvents.COMMENT_SECTION_LOADED, {
        specialId: special.id,
        comedianId: comedian.id,
        comedianSlug: comedian.slug,
        isLoggedIn,
        userFollowsComedian,
      });
    } else {
      trackEvent(CommentEvents.COMMENT_FEATURE_DISABLED, {
        specialId: special.id,
        comedianId: comedian.id,
        comedianSlug: comedian.slug,
        isLoggedIn,
      });
    }
  }, [
    isCommentsEnabled,
    special.id,
    comedian.id,
    comedian.slug,
    isLoggedIn,
    userFollowsComedian,
  ]);

  // Track error states
  useEffect(() => {
    if (error) {
      trackEvent(CommentEvents.COMMENT_SECTION_ERROR, {
        specialId: special.id,
        comedianId: comedian.id,
        error: error.message || "Unknown error",
        isLoggedIn,
        userFollowsComedian,
      });
    }
  }, [error, special.id, comedian.id, isLoggedIn, userFollowsComedian]);

  const handleCommentSubmit = async (
    content: string,
    parentId?: string,
    authorComedian?: CommentAuthorComedian | null
  ) => {
    if (!currentUser || !userFollowsComedian || isPosting) return;

    try {
      // Track submission attempt
      trackEvent(
        parentId
          ? CommentEvents.REPLY_SUBMITTED
          : CommentEvents.COMMENT_SUBMITTED,
        {
          specialId: special.id,
          comedianId: comedian.id,
          contentLength: content.length,
          isReply: !!parentId,
          parentCommentId: parentId,
          authoredAsArtist: !!authorComedian,
          authorComedianId: authorComedian?.id,
          isLoggedIn,
          userFollowsComedian,
        }
      );

      // Note: ARTIST_COMMENT_POSTED is intentionally NOT fired here. It is fired
      // in useComments' mutation onSuccess so it counts real persistence (not
      // failed posts) and only once, from the single point that knows the post
      // carried an artist identity.
      postComment({ content, parentId, authorComedian });
    } catch (error) {
      // Track submission failure
      trackEvent(
        parentId
          ? CommentEvents.REPLY_SUBMIT_FAILED
          : CommentEvents.COMMENT_SUBMIT_FAILED,
        {
          specialId: special.id,
          comedianId: comedian.id,
          error: error instanceof Error ? error.message : "Unknown error",
          contentLength: content.length,
          isReply: !!parentId,
          parentCommentId: parentId,
          authoredAsArtist: !!authorComedian,
          isLoggedIn,
          userFollowsComedian,
        }
      );
    }
  };

  const handleCommentReply = async (
    content: string,
    parentId: string,
    replyingToUsername?: string,
    authorComedian?: CommentAuthorComedian | null
  ) => {
    await handleCommentSubmit(content, parentId, authorComedian);
  };

  const handleCommentClap = async (commentId: string, clapCount: number) => {
    // Track comment clap submission
    trackEvent(CommentEvents.COMMENT_CLAP_SUBMITTED, {
      specialId: special.id,
      comedianId: comedian.id,
      commentId,
      clapCount,
      isLoggedIn,
      userFollowsComedian,
    });
  };

  const handleCommentEdit = async (commentId: string, newContent: string) => {
    if (!currentUser || isEditing) return;

    // Track comment edit
    trackEvent(CommentEvents.COMMENT_EDITED, {
      specialId: special.id,
      comedianId: comedian.id,
      commentId,
      newContentLength: newContent.length,
      isLoggedIn,
      userFollowsComedian,
    });

    editComment({ commentId, content: newContent });
  };

  const handleCommentDelete = async (commentId: string) => {
    if (!currentUser || isDeleting) return;

    // Track comment deletion
    trackEvent(CommentEvents.COMMENT_DELETED, {
      specialId: special.id,
      comedianId: comedian.id,
      commentId,
      isLoggedIn,
      userFollowsComedian,
    });

    deleteComment(commentId);
  };

  const handleLoadMore = () => {
    trackEvent(CommentEvents.COMMENTS_LOAD_MORE_CLICKED, {
      specialId: special.id,
      comedianId: comedian.id,
      currentCommentsCount: comments.length,
      hasMore,
      isLoggedIn,
      userFollowsComedian,
    });
    loadMore();
  };

  const handleRefresh = () => {
    trackEvent(CommentEvents.COMMENTS_REFRESH_CLICKED, {
      specialId: special.id,
      comedianId: comedian.id,
      currentCommentsCount: comments.length,
      newCommentsAvailable,
      isLoggedIn,
      userFollowsComedian,
    });
    refresh();
  };

  const handleNewCommentsClick = () => {
    trackEvent(CommentEvents.COMMENTS_NEW_AVAILABLE_CLICKED, {
      specialId: special.id,
      comedianId: comedian.id,
      newCommentsCount: newCommentsAvailable,
      isLoggedIn,
      userFollowsComedian,
    });
    refresh();
  };

  // Don't show comments section if feature gate is disabled
  if (!isCommentsEnabled) {
    return null;
  }

  // Handle loading state
  if (isLoading && comments.length === 0) {
    return (
      <CommentErrorBoundary>
        <div className="space-y-4 animate-pulse">
          <div>
            <div className="h-8 w-24 bg-gray-200 dark:bg-gray-700 rounded mb-4"></div>
            <div className="h-32 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
          </div>
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="space-y-2">
                <div className="flex items-center space-x-2">
                  <div className="h-8 w-8 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
                  <div className="h-4 w-32 bg-gray-200 dark:bg-gray-700 rounded"></div>
                </div>
                <div className="h-16 bg-gray-200 dark:bg-gray-700 rounded"></div>
              </div>
            ))}
          </div>
        </div>
      </CommentErrorBoundary>
    );
  }

  // Handle authentication states
  if (!isLoggedIn) {
    return (
      <CommentErrorBoundary>
        <div className="space-y-4">
          <h2 className="text-2xl font-bold">Comments</h2>
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 text-center">
            <Lock className="w-12 h-12 mx-auto mb-4 text-gray-400" />
            <p className="text-gray-600 dark:text-gray-300 mb-4">
              Sign in to view and participate in the discussion
            </p>
            <Link
              href={`/signin?redirect=/${comedian.slug}/${special.slug}`}
              className="inline-block px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Sign in
            </Link>
          </div>
        </div>
      </CommentErrorBoundary>
    );
  }

  if (!userFollowsComedian) {
    return (
      <CommentErrorBoundary>
        <div className="space-y-4">
          <h2 className="text-2xl font-bold">Comments</h2>
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-6 text-center">
            <Lock className="w-12 h-12 mx-auto mb-4 text-yellow-600 dark:text-yellow-400" />
            <p className="text-yellow-800 dark:text-yellow-200 font-semibold mb-2">
              Subscribe to {comedian.display_name} to join the conversation
            </p>
            <p className="text-yellow-700 dark:text-yellow-300 text-sm">
              Comments are exclusive to subscribers
            </p>
          </div>
        </div>
      </CommentErrorBoundary>
    );
  }

  return (
    <CommentErrorBoundary>
      {/* Desktop Version */}
      <div className="hidden lg:flex flex-col gap-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold">
            Comments {pagination.total > 0 && `(${pagination.total})`}
          </h2>

          {newCommentsAvailable > 0 && (
            <button
              onClick={handleNewCommentsClick}
              disabled={isLoading}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {newCommentsAvailable} new comment
              {newCommentsAvailable > 1 ? "s" : ""}
            </button>
          )}
        </div>

        <div className="flex flex-col gap-y-6">
          <CommentInput
            onSubmit={(content, authorComedian) =>
              handleCommentSubmit(content, undefined, authorComedian)
            }
            placeholder="Add a comment..."
            disabled={isPosting || !!rateLimitError}
            currentUser={currentUser}
            specialId={special.id}
            comedianId={comedian.id}
            isLoggedIn={isLoggedIn}
            userFollowsComedian={userFollowsComedian}
          />

          {rateLimitError && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <AlertCircle className="h-5 w-5 text-red-400" />
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-800 dark:text-red-200">
                    {rateLimitError}
                  </p>
                </div>
              </div>
            </div>
          )}

          {(error || postError || editError || deleteError) && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <AlertCircle className="h-5 w-5 text-red-400" />
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-800 dark:text-red-200">
                    {error?.message ||
                      postError?.message ||
                      editError?.message ||
                      deleteError?.message ||
                      "An error occurred"}
                  </p>
                </div>
              </div>
            </div>
          )}

          {comments.length === 0 ? (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              <p className="text-lg">Be the first to comment</p>
              <p className="text-sm mt-2">
                Share your thoughts about {special.title}
              </p>
            </div>
          ) : (
            <CommentList
              comments={comments}
              currentUserId={currentUser?.id}
              currentUser={currentUser}
              onReply={handleCommentReply}
              onClap={handleCommentClap}
              onEdit={handleCommentEdit}
              onDelete={handleCommentDelete}
              specialId={special.id}
              comedianId={comedian.id}
              isLoggedIn={isLoggedIn}
              userFollowsComedian={userFollowsComedian}
            />
          )}

          {hasMore && (
            <div className="mt-6 text-center">
              <button
                onClick={handleLoadMore}
                disabled={isLoading}
                className="px-4 py-2 text-sm font-medium text-primary hover:text-primary/80 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? "Loading..." : "Load more comments"}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* mobile Version */}
      <MobileCommentModal
        comments={comments}
        currentUser={currentUser}
        currentUserId={currentUser?.id}
        isLoading={isLoading}
        total={pagination.total}
        hasMore={hasMore}
        onSubmit={handleCommentSubmit}
        onReply={handleCommentReply}
        onClap={handleCommentClap}
        onEdit={handleCommentEdit}
        onDelete={handleCommentDelete}
        onLoadMore={handleLoadMore}
        onRefresh={handleRefresh}
        isPosting={isPosting}
        newCommentsAvailable={newCommentsAvailable}
        rateLimitError={rateLimitError}
        postError={postError}
        error={error}
        special={special}
        comedian={comedian}
        isLoggedIn={isLoggedIn}
        userFollowsComedian={userFollowsComedian}
      />
    </CommentErrorBoundary>
  );
}
